import React, { useMemo } from 'react';
import {
  HOUR_12_OPTIONS,
  QUARTER_HOUR_MINUTE_OPTIONS,
  join12HourToTime,
  splitTimeTo12Hour
} from '../../utils/datetimeQuarterHour';

const selectClassName =
  'w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-center text-sm font-semibold tabular-nums text-gray-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30';

/**
 * Compact 12-hour time picker: hour · minutes (00/15/30/45) · AM/PM.
 * Value / onChange use 24h "HH:mm" strings.
 */
export default function QuarterHourTimePicker({ value, onChange, className = '' }) {
  const { hour12, minute, period } = useMemo(() => splitTimeTo12Hour(value), [value]);

  const emit = (nextHour12, nextMinute, nextPeriod) => {
    onChange?.(join12HourToTime(nextHour12, nextMinute, nextPeriod));
  };

  return (
    <div className={`flex items-stretch gap-2 ${className}`.trim()}>
      <label className="relative min-w-0 flex-1">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Hour
        </span>
        <select
          aria-label="Hour"
          value={hour12}
          onChange={(e) => emit(Number(e.target.value), minute, period)}
          className={selectClassName}>
          {HOUR_12_OPTIONS.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
      </label>

      <label className="relative min-w-0 flex-1">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Min
        </span>
        <select
          aria-label="Minutes"
          value={minute}
          onChange={(e) => emit(hour12, Number(e.target.value), period)}
          className={selectClassName}>
          {QUARTER_HOUR_MINUTE_OPTIONS.map((m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, '0')}
            </option>
          ))}
        </select>
      </label>

      <div className="flex min-w-[5.5rem] flex-col">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Period
        </span>
        <div
          role="group"
          aria-label="AM or PM"
          className="flex h-[42px] overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm">
          {['AM', 'PM'].map((p) => {
            const active = period === p;
            return (
              <button
                key={p}
                type="button"
                aria-pressed={active}
                onClick={() => emit(hour12, minute, p)}
                className={`flex-1 text-xs font-bold tracking-wide transition ${
                  active
                    ? 'bg-blue-600 text-white shadow-inner'
                    : 'text-gray-600 hover:bg-white hover:text-gray-900'
                }`}>
                {p}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
